import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type { Supplier, PaginatedResponse } from '@/types'

interface SupplierCreate {
  codice?: string  // Auto-generated if not provided
  nome: string
  acronimo?: string
  indirizzo?: string
  citta?: string
  provincia?: string
  cap?: string
  telefono?: string
  email?: string
  partita_iva?: string
  codice_fiscale?: string
  referente_acquisti?: string
  note_commerciali?: string
}

interface SupplierUpdate {
  codice?: string
  nome?: string
  acronimo?: string
  indirizzo?: string
  citta?: string
  provincia?: string
  cap?: string
  telefono?: string
  email?: string
  partita_iva?: string
  codice_fiscale?: string
  referente_acquisti?: string
  note_commerciali?: string
  attivo?: boolean
}

/**
 * Hook per recuperare lista fornitori con paginazione e ricerca
 */
export const useSuppliers = (page: number = 1, size: number = 10, search?: string, attivo?: boolean, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['suppliers', page, size, search, attivo],
    queryFn: async () => {
      const params = new URLSearchParams({
        skip: String((page - 1) * size),
        limit: String(size),
      })
      if (search) {
        params.append('search', search)
      }
      if (attivo !== undefined) {
        params.append('attivo', String(attivo))
      }
      const response = await axiosInstance.get<PaginatedResponse<Supplier>>(
        `${API_ENDPOINTS.SUPPLIERS}/?${params.toString()}`
      )
      // Backend ritorna lista diretta, wrappa in formato PaginatedResponse
      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          total: response.data.length,
          page,
          size,
          pages: Math.ceil(response.data.length / size)
        }
      }
      return response.data
    },
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook per recuperare dettagli di un singolo fornitore
 */
export const useSupplier = (id: number) => {
  return useQuery({
    queryKey: ['supplier', id],
    queryFn: async () => {
      const response = await axiosInstance.get<Supplier>(
        `${API_ENDPOINTS.SUPPLIER_DETAIL(id)}`
      )
      return response.data
    },
    enabled: !!id,
  })
}

/**
 * Hook per creare un nuovo fornitore
 */
export const useCreateSupplier = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SupplierCreate) => {
      const response = await axiosInstance.post<Supplier>(
        `${API_ENDPOINTS.SUPPLIERS}/`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      message.success('Fornitore creato con successo')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Errore durante la creazione del fornitore'
      message.error(errorMessage)
    },
  })
}

/**
 * Hook per aggiornare dati di un fornitore
 */
export const useUpdateSupplier = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SupplierUpdate }) => {
      const response = await axiosInstance.patch<Supplier>(
        `${API_ENDPOINTS.SUPPLIER_DETAIL(id)}`,
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      queryClient.invalidateQueries({ queryKey: ['supplier', variables.id] })
      message.success('Fornitore aggiornato con successo')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'aggiornamento del fornitore'
      message.error(errorMessage)
    },
  })
}

/**
 * Hook per eliminare un fornitore (soft delete - mark as inactive)
 */
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.SUPPLIER_DETAIL(id)}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      message.success('Fornitore eliminato con successo')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'eliminazione del fornitore'
      message.error(errorMessage)
    },
  })
}
